
import Component from "vue-class-component";
import Vue from "vue";
import { SystemModule, TruckModule } from "@/store/modules";
import { getModule } from "vuex-module-decorators";
import Select from "@/components/form/Select.vue";
import { formatDateWithTime } from "@/utils";
import notifications from "../lib/notifications";
import {
  TimeframeSchedulingSettings, EditTimeframeSchedulingSettingsModel
} from "@/models/api/system/timeframeSchedulingSettings";
import permissions from "@/security/permissions";

const systemModule = getModule(SystemModule);

@Component({
  components: {
    Select
  }
})
export default class ImportTrucks extends Vue {
  facilityCode: string = null;
  loading: boolean = false;
  record: TimeframeSchedulingSettings = null;
  defaultRecord: TimeframeSchedulingSettings = null;
  editModel: EditTimeframeSchedulingSettingsModel = null;
  edittingDefault: boolean = false;

  windowLengthInformation: any = {
    hours: "00",
    minutes: "00",
    seconds: "00",
    modalIsVisible: false
  };

  overheadTimeInformation: any = {
    hours: "00",
    minutes: "00",
    seconds: "00",
    modalIsVisible: false
  };

  bufferTimeInformation: any = {
    hours: "00",
    minutes: "00",
    seconds: "00",
    modalIsVisible: false
  };

  defaultCommodityUnloadTimeInformation: any = {
    hours: "00",
    minutes: "00",
    seconds: "00",
    modalIsVisible: false
  };

  async created() {
    if (systemModule.facilities && systemModule.facilities.length == 1)
    {
      this.facilityCode = systemModule.facilities[0].facilityCode;
      await this.loadData();
    }

    if(this.hasPermission(permissions.manage.timeframeSchedulingDefaultSettings))
    {
      this.defaultRecord = await systemModule.getTimeframeSchedulingDefaultSettings();
    }
  }

  async loadData() {
    this.loading = true;
    try {
      this.record = await systemModule.getTimeframeSchedulingSettings({
        facilityCode: this.facilityCode
      });
    } catch (ex) {
      notifications.error("Search Failed", "Failed to load records", ex);
    }
    this.loading = false;
  }

  format(dateToFormat) {
    return formatDateWithTime(dateToFormat);
  }

  goBack() {
    this.$router.back();
  }

  async filter() {
    await this.loadData();
  }

  editRecord(record: TimeframeSchedulingSettings = null) {
    this.edittingDefault = false;
    const model = new EditTimeframeSchedulingSettingsModel();
    if (record) {
      const windowData = record.timeFrameWindowLength.split(":");
      const overheadData = record.overheadTime.split(":");
      const bufferData = record.bufferPriorToArrivalTime.split(":")
      const defaultCommodityUnloadTimeData = record.defaultCommodityUnloadTime.split(":")
      model.earliestStart = record.earliestStart;
      model.defaultStartTime = record.defaultStartTime;
      model.facilityCode = record.facilityCode ? record.facilityCode : this.facilityCode;
      this.windowLengthInformation.hours = windowData[0];
      this.windowLengthInformation.minutes = windowData[1];
      this.windowLengthInformation.seconds = windowData[2] ? windowData[2] : "00";
      this.overheadTimeInformation.hours = overheadData[0];
      this.overheadTimeInformation.minutes = overheadData[1];
      this.overheadTimeInformation.seconds = overheadData[2] ? overheadData[2] : "00";
      this.bufferTimeInformation.hours = bufferData[0];
      this.bufferTimeInformation.minutes = bufferData[1];
      this.bufferTimeInformation.seconds = bufferData[2] ? bufferData[2] : "00";
      this.defaultCommodityUnloadTimeInformation.hours = defaultCommodityUnloadTimeData[0];
      this.defaultCommodityUnloadTimeInformation.minutes = defaultCommodityUnloadTimeData[1];
      this.defaultCommodityUnloadTimeInformation.seconds = defaultCommodityUnloadTimeData[2] ? defaultCommodityUnloadTimeData[2] : "00";
    } else {
      model.facilityCode = this.facilityCode;
      this.windowLengthInformation.hours = "00";
      this.windowLengthInformation.minutes = "00";
      this.windowLengthInformation.seconds = "00";
      this.overheadTimeInformation.hours = "00";
      this.overheadTimeInformation.minutes = "00";
      this.overheadTimeInformation.seconds = "00";
      this.bufferTimeInformation.hours = "00";
      this.bufferTimeInformation.minutes = "00";
      this.bufferTimeInformation.seconds = "00";
      this.defaultCommodityUnloadTimeInformation.hours = "00";
      this.defaultCommodityUnloadTimeInformation.minutes = "00";
      this.defaultCommodityUnloadTimeInformation.seconds = "00";
    }

    this.editModel = model;
  }
  
  editDefaultRecord()
  {
    this.editRecord(this.defaultRecord);
    this.edittingDefault = true;
  }

  closeEditModal() {
    this.edittingDefault = false;
    this.editModel.visible = false;
    this.editModel = null;
  }

  async saveRecord() {
    try {
      this.editModel.saving = true;
      this.editModel.timeFrameWindowLength =
        this.windowLengthInformation.hours +
        ":" +
        this.windowLengthInformation.minutes +
        ":" +
        this.windowLengthInformation.seconds;
      this.editModel.bufferPriorToArrivalTime =
        this.bufferTimeInformation.hours +
        ":" +
        this.bufferTimeInformation.minutes +
        ":" +
        this.bufferTimeInformation.seconds;
      this.editModel.overheadTime =
        this.overheadTimeInformation.hours +
        ":" +
        this.overheadTimeInformation.minutes +
        ":" +
        this.overheadTimeInformation.seconds;
      this.editModel.defaultCommodityUnloadTime =
        this.defaultCommodityUnloadTimeInformation.hours +
        ":" +
        this.defaultCommodityUnloadTimeInformation.minutes +
        ":" +
        this.defaultCommodityUnloadTimeInformation.seconds;

      if(this.edittingDefault) {
        await systemModule.saveTimeframeSchedulingDefaultSettings(this.editModel.toCreateRequest());
        this.defaultRecord = await systemModule.getTimeframeSchedulingDefaultSettings();
      } else {
        await systemModule.saveTimeframeSchedulingSettings(this.editModel.toCreateRequest());
      }
      if(this.record)
        await this.loadData();
      this.editModel.visible = false;
      this.editModel = null;
      this.edittingDefault = false;
      notifications.success("Save Success", "Record successfully saved");
    } catch (ex) {
      this.editModel.saving = false;
      notifications.error("Save Failed", "Failed to save record", ex);
    }
  }

  async deleteRecord(record: TimeframeSchedulingSettings) {
    const confirmed = await this.$bvModal.msgBoxConfirm(
      "Are you sure you want to remove this override? This action cannot be undone."
    );
    if (confirmed) {
      try {
        await systemModule.resetTimeframeSchedulingSettings(record.facilityCode);
        await this.loadData();
        notifications.success(
          "Removed timeframe scheduling settings override",
          "Record successfully deleted."
        );
      } catch (ex) {
        notifications.error(
          "Removed timeframe scheduling settings override",
          "Failed to delete record",
          ex
        );
      }
    }
  }

  get canFilter() {
    return this.facilityCode != null;
  }

  get showFacilitySelect() {
    return systemModule.facilities && systemModule.facilities.length != 1;
  }
}
